/*
The Application belongs to Comprehend PT Inc.
    Author: Jacob Michalski
    Date Created: 12/13/2023
*/
/* global chrome */
// The microphone permission screen Chrome opens right after the extension is
// installed (background.js requestMicrophone → index.html#micRequest). It is
// the very first Comprehend screen most people ever see, so it follows the
// playground onboarding design (mint on near-white, glass card, pill button —
// see cx_embed/apps/playground/public/pg-theme.css) and says plainly what the
// permission is for: we only ever listen after you press Record.
import { useEffect, useState } from 'react';

// Where the Playground lives — same list as background.js PLAYGROUND_URL_PATTERNS.
const PLAYGROUND_URLS = [
  'https://playground.comprehendpt.ai/*',
  'https://playground.dev.comprehendpt.com/*',
  'http://localhost:3009/*',
];

const COPY = {
  ask: {
    emoji: '🎙️',
    title: 'One quick permission',
    body:
      'Comprehend needs your microphone to record visits. It only ever listens after you press Record — never in the background, never on its own.',
  },
  requesting: {
    emoji: '👀',
    title: 'Look up top',
    body: 'Chrome is asking right now — pick “Allow while visiting the site” in the popup by the address bar.',
  },
  blocked: {
    emoji: '🔒',
    title: 'Microphone is blocked in Chrome',
    body:
      'No problem. We opened Chrome’s permission settings for you — flip Microphone to “Allow”, and this page will notice on its own.',
  },
  nomic: {
    emoji: '🔌',
    title: 'No microphone found',
    body: 'Plug one in (or turn on your laptop mic) and try again.',
  },
  error: {
    emoji: '🤔',
    title: 'That didn’t work',
    body: 'Try again — if it keeps happening, the Need Help? bubble in Comprehend gets you a human.',
  },
  granted: {
    emoji: '✅',
    title: 'You’re set',
    body: 'Microphone allowed. Taking you back to the Playground…',
  },
};

const styles = {
  page: {
    position: 'fixed',
    inset: 0,
    display: 'grid',
    placeItems: 'center',
    padding: '24px 16px',
    background: 'linear-gradient(180deg, #f7fbf9 0%, #eef8f3 100%)',
    color: '#1a1e1c',
    fontFamily:
      '"Outfit", -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif',
    overflow: 'hidden',
  },
  orb: (extra) => ({
    position: 'absolute',
    borderRadius: '50%',
    filter: 'blur(60px)',
    opacity: 0.55,
    pointerEvents: 'none',
    ...extra,
  }),
  card: {
    position: 'relative',
    width: 'min(460px, 100%)',
    background: '#fff',
    border: '1px solid #e3ebe7',
    borderRadius: 24,
    padding: '28px 26px 24px',
    boxShadow: '0 24px 60px -24px rgba(26,30,28,.28)',
    textAlign: 'center',
    boxSizing: 'border-box',
  },
  badge: {
    width: 92,
    height: 92,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #b0ffe2, #2dce8f)',
    display: 'grid',
    placeItems: 'center',
    fontSize: 42,
    margin: '0 auto 16px',
    boxShadow: '0 20px 40px -12px rgba(45,206,143,.6)',
  },
  title: { fontSize: 26, fontWeight: 600, letterSpacing: '-.02em', margin: '0 0 8px', lineHeight: 1.15 },
  body: { fontSize: 15.5, lineHeight: 1.5, color: '#4b534f', margin: '0 0 18px' },
  cta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    background: '#2dce8f',
    color: '#fff',
    fontWeight: 600,
    borderRadius: 999,
    padding: '12px 24px',
    fontSize: 15,
    border: 0,
    cursor: 'pointer',
    boxShadow: '0 8px 20px -8px rgba(45,206,143,.7)',
    fontFamily: 'inherit',
  },
  ghost: {
    display: 'inline-flex',
    alignItems: 'center',
    background: 'transparent',
    color: '#4b534f',
    border: '1px solid #c9d6d0',
    borderRadius: 999,
    padding: '11px 20px',
    fontSize: 14,
    cursor: 'pointer',
    fontFamily: 'inherit',
    marginLeft: 8,
  },
  steps: { textAlign: 'left', margin: '0 0 18px', padding: 0, listStyle: 'none', display: 'grid', gap: 8 },
  step: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    background: '#f6faf8',
    border: '1px solid #e3ebe7',
    borderRadius: 12,
    padding: '9px 11px',
    fontSize: 13.5,
    color: '#1a1e1c',
  },
  n: {
    flex: 'none',
    width: 22,
    height: 22,
    borderRadius: '50%',
    background: '#e5f5ee',
    color: '#1fae76',
    fontSize: 12,
    fontWeight: 600,
    display: 'grid',
    placeItems: 'center',
  },
  waiting: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 13, color: '#7d8683', marginTop: 12 },
  pulse: { width: 9, height: 9, borderRadius: '50%', background: '#f5b942', display: 'inline-block' },
  fine: { fontSize: 12.5, color: '#7d8683', marginTop: 16, lineHeight: 1.45 },
};

const MicRequest = () => {
  const [state, setState] = useState('ask'); // ask | requesting | blocked | nomic | error | granted

  // This view renders inside the Auth layout, whose navbar + green hero
  // ("Welcome to Comprehend PT!") showed through behind our page. Hide that
  // chrome while we're mounted; the layout is untouched for login/register.
  useEffect(() => {
    const hidden = [];
    document
      .querySelectorAll('.main-content > .navbar, .main-content > .header, .main-content > .header + .separator')
      .forEach((el) => {
        hidden.push([el, el.style.display]);
        el.style.display = 'none';
      });
    return () => hidden.forEach(([el, d]) => { el.style.display = d; });
  }, []);

  useEffect(() => {
    checkCurrentPermissionState();
    let pollInterval;
    if (state === 'blocked') {
      pollInterval = setInterval(checkCurrentPermissionState, 1000);
    }
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const checkCurrentPermissionState = async () => {
    try {
      const permission = await navigator.permissions.query({ name: 'microphone' });
      if (permission.state === 'granted') {
        handlePermissionGranted();
      }
      permission.addEventListener('change', () => {
        if (permission.state === 'granted') handlePermissionGranted();
      });
    } catch (error) {
      console.error('Error checking permission state:', error);
    }
  };

  const handlePermissionGranted = () => {
    setState('granted');
    localStorage.removeItem('microphoneBlocked');
    localStorage.setItem('microphonePermissionGranted', 'true');

    if (chrome && chrome.runtime) {
      try {
        chrome.runtime.sendMessage({ type: 'MIC_PERMISSION_GRANTED', timestamp: Date.now() }, () => {
          void chrome.runtime.lastError;
        });
      } catch (error) {
        console.log('Could not send message to background script:', error);
      }
    }

    // Close the Chrome settings tab we opened, if any.
    const settingsTabId = localStorage.getItem('settingsTabId');
    if (settingsTabId && chrome && chrome.tabs) {
      chrome.tabs.remove(parseInt(settingsTabId, 10), () => {
        void chrome.runtime.lastError;
        localStorage.removeItem('settingsTabId');
      });
    }

    // Give the ✅ a beat to land, then get out of the way. If the install came
    // from the Playground, put its tab in front first — closing this tab
    // otherwise drops the user back on the Chrome Web Store, and the
    // installer they were following is on the Playground tab.
    setTimeout(() => {
      if (!(chrome && chrome.tabs)) { window.close(); return; }
      chrome.tabs.query({ url: PLAYGROUND_URLS }, (tabs) => {
        const pg = tabs && tabs[0];
        const finish = () => chrome.tabs.getCurrent((tab) => { if (tab) chrome.tabs.remove(tab.id); else window.close(); });
        if (!pg) { finish(); return; }
        chrome.tabs.update(pg.id, { active: true }, () => {
          chrome.windows.update(pg.windowId, { focused: true }, finish);
        });
      });
    }, 900);
  };

  const openPermissionSettings = () => {
    if (chrome && chrome.runtime && chrome.tabs) {
      const extensionId = chrome.runtime.id;
      const settingsUrl = `chrome://settings/content/siteDetails?site=chrome-extension%3A%2F%2F${extensionId}`;
      chrome.tabs.create({ url: settingsUrl }, (tab) => {
        if (tab) localStorage.setItem('settingsTabId', tab.id.toString());
      });
    }
  };

  function requestMicrophoneAccess() {
    setState('requesting');
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(function (stream) {
        handlePermissionGranted();
        stream.getTracks().forEach((track) => track.stop());
      })
      .catch(function (err) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setState('blocked');
          localStorage.setItem('microphoneBlocked', 'true');
          setTimeout(openPermissionSettings, 1000);
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          setState('nomic');
        } else {
          console.error('Failed to access microphone:', err);
          setState('error');
        }
      });
  }

  const c = COPY[state];
  const granted = state === 'granted';

  return (
    <div style={styles.page}>
      <div
        style={styles.orb({
          width: '60vmax',
          height: '60vmax',
          background: 'radial-gradient(circle at 30% 30%, #2dce8f, transparent 62%)',
          top: '-25vmax',
          left: '-15vmax',
        })}
      />
      <div
        style={styles.orb({
          width: '45vmax',
          height: '45vmax',
          background: 'radial-gradient(circle, #b0ffe2, transparent 62%)',
          right: '-10vmax',
          bottom: '-20vmax',
        })}
      />
      <div style={styles.card} role="dialog" aria-live="polite">
        <div style={styles.badge}>{c.emoji}</div>
        <h1 style={styles.title}>{c.title}</h1>
        <p style={styles.body}>{c.body}</p>

        {state === 'ask' && (
          <ul style={styles.steps}>
            <li style={styles.step}>
              <span style={styles.n}>1</span>
              <span>
                Click <b>Allow microphone</b> below.
              </span>
            </li>
            <li style={styles.step}>
              <span style={styles.n}>2</span>
              <span>
                Pick <b>Allow while visiting the site</b> in Chrome’s popup. (“Allow this time” works too, but Chrome will ask again next visit.)
              </span>
            </li>
            <li style={styles.step}>
              <span style={styles.n}>3</span>
              <span>This tab closes itself. Back to your EMR — or the Playground.</span>
            </li>
          </ul>
        )}

        {!granted && (
          <div>
            <button type="button" style={styles.cta} onClick={requestMicrophoneAccess}>
              {state === 'ask' ? 'Allow microphone' : 'Try again'}
            </button>
            {state === 'blocked' && (
              <button type="button" style={styles.ghost} onClick={openPermissionSettings}>
                Open settings again
              </button>
            )}
          </div>
        )}

        {state === 'blocked' && (
          <div style={styles.waiting}>
            <span style={styles.pulse} /> Waiting for the microphone to be allowed…
          </div>
        )}

        <div style={styles.fine}>
          {granted
            ? 'Recording only happens on a visit, only after you press Record.'
            : 'Installing from the Comprehend Playground? Its installer picks up right where you left off once this is done.'}
        </div>
      </div>
    </div>
  );
};

export default MicRequest;
